/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
    Email,
    Key,
    LocalFireDepartment,
    Notifications,
    QuestionAnswer,
    SatelliteAlt,
} from '@mui/icons-material';
import { Checkbox, Divider, FormControlLabel, Stack, Typography } from '@mui/material';
import { User, UserNotificationSettings } from '../../database/user';

function getSettingValue(
    notificationSettings: UserNotificationSettings,
    path: string,
): boolean {
    const components = path.split('.');

    let currentSetting: any = notificationSettings;
    for (const component of components) {
        if (currentSetting === undefined || currentSetting === null) {
            return false;
        }
        currentSetting = currentSetting[component];
    }
    return Boolean(currentSetting);
}

function setSettingValue(
    notificationSettings: UserNotificationSettings,
    path: string,
    value: boolean,
): UserNotificationSettings {
    const components = path.split('.');
    const result = Object.assign({}, notificationSettings);

    let currentResultSetting: any = result;
    let currentOriginalSetting: any = notificationSettings;

    for (let i = 0; i < components.length - 1; i++) {
        const component = components[i];
        currentResultSetting[component] = { ...currentOriginalSetting[component] };

        currentResultSetting = currentResultSetting[component];
        currentOriginalSetting = currentOriginalSetting[component];
    }

    currentResultSetting[components[components.length - 1]] = value;

    return result;
}

interface NotificationSettingsSection {
    label: string;
    settings: { label: string; path: string }[];
    icon: React.ReactNode;
}

const sections: NotificationSettingsSection[] = [
    {
        label: 'Artist',
        icon: <LocalFireDepartment />,
        settings: [
            {
                label: 'Notify me via email when a user submits a request',
                path: 'artistNotificationSettings.enableNewRequestsEmail',
            },
        ],
    },
    {
        label: 'Q&A',
        icon: <QuestionAnswer />,
        settings: [
            {
                label: 'Notify me when I receive a response to my request',
                path: 'emailNotificationSettings.requestResponseEmail',
            },
        ],
    },
    {
        label: 'ATWW',
        icon: <Key />,
        settings: [
            {
                label: 'Get notified about new ATWW posts',
                path: 'emailNotificationSettings.communityPostCreatedEmail',
            },
            {
                label: 'Notify me when someone comments on my ATWW post',
                path: 'emailNotificationSettings.communityPostCommentEmail',
            },
            {
                label: 'Notify me when someone tags me in an ATWW post',
                path: 'emailNotificationSettings.communityPostTagEmail',
            },
            {
                label: 'Notify me when someone tags me in an ATWW comment',
                path: 'emailNotificationSettings.communityPostCommentTagEmail',
            },
            {
                label: 'Subscribed users only: Notify me when a new exclusive post is created',
                path: 'emailNotificationSettings.exclusiveCommunityPostCreatedEmail',
            },
        ],
    },
    {
        label: 'ESS',
        icon: <SatelliteAlt />,
        settings: [
            {
                label: 'Let me know about new topics',
                path: 'emailNotificationSettings.forumsTopicCreatedEmail',
            },
            {
                label: 'Notify me when Abstract creates a new topic',
                path: 'emailNotificationSettings.forumsArtistTopicCreatedEmail',
            },
            {
                label: 'Notify me when Abstract adds a thread',
                path: 'emailNotificationSettings.forumsArtistThreadCreatedEmail',
            },
            {
                label: 'Get notified about replies to my threads',
                path: 'emailNotificationSettings.forumsThreadReplyEmail',
            },
            {
                label: 'Notify me when someone tags me in a thread',
                path: 'emailNotificationSettings.forumsThreadTagEmail',
            },
        ],
    },
];

interface NotificationSettingsEditorProps {
    user: User;
    notificationSettings: UserNotificationSettings;
    setNotificationSettings?: (value: UserNotificationSettings) => void;
    disabled?: boolean;
}

const NotificationSettingsEditor: React.FC<NotificationSettingsEditorProps> = ({
    user,
    notificationSettings,
    setNotificationSettings,
    disabled,
}) => {
    return (
        <Stack spacing={2}>
            <Stack
                id='notifications'
                sx={{
                    scrollMarginTop: 'calc(var(--navbar-height) + 8px)',
                }}
            >
                <Typography variant='h5'>
                    <Notifications
                        style={{ verticalAlign: 'middle', marginRight: '0.1em' }}
                    />{' '}
                    Notifications
                </Typography>
                <Divider />
            </Stack>

            {sections
                .filter((s) => s.label !== 'Artist' || user.isArtist)
                .map((s) => (
                    <Stack key={s.label} spacing={0.5}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            {s.icon}
                            <Typography
                                id={`notifications-${s.label.toLowerCase()}`}
                                variant='h6'
                                sx={{
                                    scrollMarginTop: '88px',
                                }}
                            >
                                {s.label}
                            </Typography>
                        </Stack>

                        {s.settings.map((setting) => (
                            <FormControlLabel
                                key={setting.path}
                                control={
                                    <Checkbox
                                        checked={getSettingValue(
                                            notificationSettings,
                                            setting.path,
                                        )}
                                        onChange={
                                            !disabled && setNotificationSettings
                                                ? (e) =>
                                                      setNotificationSettings(
                                                          setSettingValue(
                                                              notificationSettings,
                                                              setting.path,
                                                              e.target.checked,
                                                          ),
                                                      )
                                                : undefined
                                        }
                                    />
                                }
                                label={setting.label}
                            />
                        ))}
                    </Stack>
                ))}
        </Stack>
    );
};

export default NotificationSettingsEditor;
